import { Marker, Polyline } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import type { LatLngExpression } from "leaflet";
import { getShipPosition, bearingDegrees } from "./world-map-utils";
import type { WorldMapRoute } from "./world-map-utils";
import { createShipMarkerIcon } from "./world-map-ship-marker";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";

export interface WorldMapRoutesAndMarkersProps {
    routes: WorldMapRoute[];
    progressById: Record<string, number>;
    selectedRouteId: string | null;
    isContainerActive: (containerId: string) => boolean;
    onMarkerClick: (containerId: string, position: [number, number]) => void;
}

export function WorldMapRoutesAndMarkers({
    routes,
    progressById,
    selectedRouteId,
    isContainerActive,
    onMarkerClick,
}: WorldMapRoutesAndMarkersProps) {
    return (
        <>
            {/* Route lines - only for selected route */}
            {selectedRouteId && routes
                .filter(({ c }) => c.id === selectedRouteId)
                .map(({ c, coordinates, color }) => {
                    const isActive = isContainerActive(c.id);
                    const lineWeight = isActive ? 6 : 3;
                    return (
                        <Polyline
                            key={`line-${c.id}`}
                            positions={coordinates}
                            pathOptions={{
                                color,
                                weight: lineWeight,
                                opacity: 1,
                            }}
                        />
                    );
                })}

            {/* Ship markers (clustered for performance) */}
            <MarkerClusterGroup>
                {routes.map(({ c, coordinates }) => {
                    const t = progressById[c.id] ?? 0;
                    const shipPosResult = getShipPosition(coordinates, t);
                    if (!shipPosResult) return null;
                    const [lat, lng] = shipPosResult;
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                    const shipPos = shipPosResult as LatLngExpression;

                    const destination = coordinates[coordinates.length - 1];
                    const bearing = destination ? bearingDegrees([lat, lng], destination) : 0;
                    const isActive = isContainerActive(c.id);
                    const markerIcon = createShipMarkerIcon({ isActive, bearing });

                    return (
                        <Marker
                            key={`route-${c.id}`}
                            position={shipPos}
                            icon={markerIcon}
                            zIndexOffset={isActive ? 1000 : 0}
                            eventHandlers={{
                                click: () => {
                                    onMarkerClick(c.id, shipPosResult);
                                },
                            }}
                        />
                    );
                })}
            </MarkerClusterGroup>
        </>
    );
}
